import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { TeamsProvider } from './hooks/useTeams';
import { GoogleMapsLoader } from './components/GoogleMapsLoader';
import { LoginGate } from './components/LoginGate';
import { PendingOverlay } from './components/PendingOverlay';
import { TabNavigation } from './components/TabNavigation';
import { Dashboard } from './pages/Dashboard';
import { VisitForm } from './pages/VisitForm';
import { VisitsList } from './pages/VisitsList';
import { MapView } from './pages/MapView';
import { FollowUp } from './pages/FollowUp';
import { Catalog } from './pages/Catalog';
import { Member } from './pages/Member';
import { Profile } from './pages/Profile';
import { Admin } from './pages/Admin';
import { TABS } from '@ns-b2b/shared';

function AppContent() {
    const { user, isLoading, isAuthenticated } = useAuth();
    const [activeTab, setActiveTab] = useState('visit-form');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, []);

    useEffect(() => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', activeTab);
        window.history.replaceState({}, '', url.toString());
    }, [activeTab]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="spinner w-10 h-10 mx-auto mb-4" />
                    <p className="text-neutral-500 text-sm">กำลังโหลด...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated || !user) {
        return <LoginGate />;
    }

    if (user.status === 'pending') {
        return <PendingOverlay user={user} />;
    }

    const visibleTabs = TABS.filter((tab) => {
        if (!tab.roles.includes(user.effectiveRole)) return false;
        if (tab.hideFor?.includes(user.effectiveRole)) return false;
        return true;
    });

    const renderTabContent = () => {
        switch (activeTab) {
            case 'dashboard': return <Dashboard />;
            case 'visit-form': return <VisitForm />;
            case 'visits-list': return <VisitsList />;
            case 'map': return <MapView />;
            case 'followup': return <FollowUp />;
            case 'catalog': return <Catalog />;
            case 'member': return <Member />;
            case 'profile': return <Profile />;
            case 'admin': return <Admin />;
            default: return <VisitForm />;
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            {/* Header */}
            <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-soft">
                                <span className="text-white font-bold text-lg">NS</span>
                            </div>
                            <div className="hidden sm:block">
                                <span className="font-bold text-neutral-900">Small B2B</span>
                                <span className="text-neutral-400 ml-2 text-sm">Sales Tracker</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-neutral-600 hidden sm:block font-medium">
                                {user.name}
                            </span>
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt={user.name}
                                    className="w-9 h-9 rounded-full object-cover ring-2 ring-neutral-100"
                                />
                            ) : (
                                <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                                    <span className="text-white font-semibold text-sm">
                                        {user.name.charAt(0)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Tab Navigation */}
            <div className="bg-white border-b border-neutral-200 sticky top-16 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3">
                    <TabNavigation
                        tabs={visibleTabs}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-4 py-6">
                <div className="animate-in">{renderTabContent()}</div>
            </main>
        </div>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <TeamsProvider>
                <GoogleMapsLoader>
                    <AppContent />
                </GoogleMapsLoader>
            </TeamsProvider>
        </AuthProvider>
    );
}
